import { useEffect, useLayoutEffect, useMemo, useRef, useState, type TouchEvent } from 'react';
import './index.css';
import { ActionButtons } from './components/ActionButtons';
import { FilterBar } from './components/FilterBar';
import { InfoBar } from './components/InfoBar';
import { NavBar } from './components/NavBar';
import { ProgressWrap } from './components/ProgressWrap';
import { QuestionCard } from './components/QuestionCard';
import { ResultModal, type QuizResult } from './components/ResultModal';
import { Toast } from './components/Toast';
import { UploadCard } from './components/UploadCard';
import type { Answers, Bank } from './types';
import { checkAnswer, buildWrongBank, downloadJson, isMobile, localDate } from './utils';

export function App() {
  const [data, setData] = useState<Bank | null>(null);
  const [answers, setAnswers] = useState<Answers>(new Map());
  const [current, setCurrent] = useState(0);
  const [chapterFilter, setChapterFilter] = useState('全部');
  const [typeFilter, setTypeFilter] = useState('全部');
  const [filterOpen, setFilterOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [session, setSession] = useState(0); // 重新开始时递增，用于重置题目卡片内部状态
  const [toast, setToast] = useState({ msg: '', show: false });
  const [result, setResult] = useState<QuizResult | null>(null);
  const [selected, setSelected] = useState<string[]>([]); // 当前题未提交的选项草稿
  const [blank, setBlank] = useState(''); // 当前题未提交的填空草稿
  const toastTimer = useRef<number | undefined>(undefined);
  const mainRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const questions = useMemo(() => {
    if (!data) return [];
    return data.questions.filter(q =>
      (chapterFilter === '全部' || q.chapter === chapterFilter) &&
      (typeFilter === '全部' || q.type === typeFilter)
    );
  }, [data, chapterFilter, typeFilter]);

  // 筛选后当前索引越界时回到开头
  useEffect(() => {
    if (current >= questions.length) setCurrent(0);
  }, [questions.length, current]);

  function toastShow(msg: string) {
    setToast({ msg, show: true });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(t => ({ ...t, show: false })), 2000);
  }

  // 重置答题进度与筛选条件
  function resetProgress() {
    setAnswers(new Map());
    setCurrent(0);
    setChapterFilter('全部');
    setTypeFilter('全部');
    setFilterOpen(false);
    setNavOpen(false);
    setResult(null);
    setSession(s => s + 1);
  }

  // 重新导入：清除当前题库，回到导入页
  function reimport() {
    resetProgress();
    setData(null);
  }

  // 导出错题：将当前答错的题目打包为与导入格式一致的 JSON 文件下载
  function exportWrong() {
    if (!data) return;
    const wrongBank = buildWrongBank(data, answers);
    if (!wrongBank) {
      toastShow('暂无错题可导出');
      return;
    }
    const name = data.subject || '题库';
    downloadJson(wrongBank, `${name}-错题-${localDate().replace(/-/g, '')}.json`);
    toastShow(`已导出 ${wrongBank.total} 道错题`);
  }

  function loadFile(file: File) {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = JSON.parse(String(e.target?.result));
        if (!parsed || !Array.isArray(parsed.questions)) {
          throw new Error('文件中未找到 questions 数组，格式不符');
        }
        // 为每个题目补充默认字段
        parsed.questions.forEach((q: { id?: number | string; options?: Record<string, string>; explanation?: string }, i: number) => {
          if (q.id == null) q.id = i + 1;
          if (!q.options) q.options = {};
          if (!q.explanation) q.explanation = '';
        });
        setData(parsed);
        resetProgress();
        toastShow('导入成功：' + (parsed.subject || file.name));
      } catch (err) {
        toastShow('导入失败：' + (err as Error).message);
        console.error(err);
      }
    };
    reader.onerror = () => toastShow('文件读取失败');
    reader.readAsText(file, 'utf-8');
  }

  function finish() {
    const answeredCount = questions.filter(q => answers.has(q.id)).length;
    if (answeredCount < questions.length) {
      toastShow('还有题目未作答，可先完成后再重新开始');
      return;
    }
    const ok = questions.filter(q => answers.get(q.id)?.ok).length;
    const rate = questions.length ? Math.round((ok / questions.length) * 100) : 0;
    setResult({ total: questions.length, ok, rate });
  }

  function restart() {
    setAnswers(new Map());
    setCurrent(0);
    setSession(s => s + 1);
    setResult(null);
  }

  function changeChapter(v: string) {
    setChapterFilter(v);
    setCurrent(0);
    if (isMobile()) setFilterOpen(false);
  }

  function changeType(v: string) {
    setTypeFilter(v);
    setCurrent(0);
    if (isMobile()) setFilterOpen(false);
  }

  function jumpTo(i: number) {
    setCurrent(i);
    if (isMobile()) setNavOpen(false);
  }

  const q = questions[current];

  // 切题、重新开始或筛选变化后：内容区回到顶部，并清空未提交的草稿（已答题回显答案）
  useLayoutEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
    setSelected([]);
    const prev = q ? answers.get(q.id) : undefined;
    setBlank(prev ? prev.answer : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, session, chapterFilter, typeFilter]);

  function currentAnswer() {
    if (!q) return '';
    return q.type === 'blank' ? blank.trim() : selected.join('');
  }

  function submitCurrent() {
    if (!q) return;
    const ans = currentAnswer();
    if (!ans) {
      toastShow('请先作答再提交');
      return;
    }
    const ok = checkAnswer(q, ans);
    setAnswers(prev => {
      const next = new Map(prev);
      next.set(q.id, { ok, answer: ans });
      return next;
    });
  }

  // App 式手势：在内容区左滑下一题、右滑上一题
  function onTouchStart(e: TouchEvent<HTMLDivElement>) {
    // 抽屉或结果弹窗打开时、多指触控、输入框内滑动不响应翻页手势
    if (filterOpen || navOpen || result || (e.target as HTMLElement).tagName === 'INPUT' || e.touches.length > 1) {
      touchStart.current = null;
      return;
    }
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  }

  function onTouchEnd(e: TouchEvent<HTMLDivElement>) {
    const s = touchStart.current;
    touchStart.current = null;
    if (!s || !q || e.touches.length > 0) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    // 水平位移足够大且明显大于纵向位移时，才视为翻页手势（避免误触竖向滚动）
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    if (dx < 0) {
      if (!answers.has(q.id)) {
        toastShow('请先作答，再进入下一题');
        return;
      }
      if (current < questions.length - 1) setCurrent(c => c + 1);
    } else if (current > 0) {
      setCurrent(c => c - 1);
    }
  }

  return (
    <>
      <div className="header">
        <div className="header-text">
          <h1>📚 题宝</h1>
          <div className="sub">{data ? (data.subject || '题宝') : '导入 JSON 题库文件，开始做题'}</div>
        </div>
        {data && (
          <div className="header-btns">
            <button className="header-btn" onClick={exportWrong}>导出错题</button>
            <button className="header-btn" onClick={reimport}>重新导入</button>
          </div>
        )}
      </div>

      <div
        ref={mainRef}
        className={`container${!data ? ' welcome' : ''}${filterOpen || navOpen ? ' locked' : ''}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {!data && <UploadCard onFile={loadFile} />}

        {data && (
          <>
            <InfoBar bank={data} />

            <FilterBar
              bank={data}
              chapterFilter={chapterFilter}
              typeFilter={typeFilter}
              open={filterOpen}
              onToggle={() => setFilterOpen(v => !v)}
              onChapter={changeChapter}
              onType={changeType}
              onReset={() => { setChapterFilter('全部'); setTypeFilter('全部'); setCurrent(0); }}
            />

            <ProgressWrap questions={questions} answers={answers} current={current} />

            {q ? (
              <>
                {/* key 包含会触发"重新作答"的因素，确保切换/重置时清空未提交的选中状态 */}
                <QuestionCard
                  key={`${session}-${chapterFilter}-${typeFilter}-${current}`}
                  question={q}
                  num={current + 1}
                  answerState={answers.get(q.id)}
                  selected={selected}
                  blank={blank}
                  onSelectedChange={setSelected}
                  onBlankChange={setBlank}
                  onSubmit={submitCurrent}
                  isFirst={current === 0}
                  isLast={current === questions.length - 1}
                  onPrev={() => setCurrent(c => Math.max(0, c - 1))}
                  onNext={() => setCurrent(c => Math.min(questions.length - 1, c + 1))}
                  onFinish={finish}
                />
                <NavBar
                  questions={questions}
                  answers={answers}
                  current={current}
                  open={navOpen}
                  onToggle={() => setNavOpen(v => !v)}
                  onJump={jumpTo}
                />
              </>
            ) : (
              <div className="empty-tip">当前筛选条件下没有题目</div>
            )}
          </>
        )}
      </div>

      {/* 移动端底部操作栏：筛选 / 导航 / 答题操作常驻（桌面端隐藏，操作按钮在题目卡内） */}
      {data && (
        <div className="action-bar">
          <button className={`tool-btn${filterOpen ? ' active' : ''}`} onClick={() => setFilterOpen(v => !v)}>
            <span className="icon">⚙</span>
            <span>筛选</span>
          </button>
          <button className={`tool-btn${navOpen ? ' active' : ''}`} onClick={() => setNavOpen(v => !v)}>
            <span className="icon">☰</span>
            <span>导航</span>
          </button>
          {q && (
            <ActionButtons
              isFirst={current === 0}
              isLast={current === questions.length - 1}
              answered={answers.has(q.id)}
              onPrev={() => setCurrent(c => Math.max(0, c - 1))}
              onSubmit={submitCurrent}
              onNext={() => setCurrent(c => Math.min(questions.length - 1, c + 1))}
              onFinish={finish}
            />
          )}
        </div>
      )}

      <Toast message={toast.msg} visible={toast.show} />
      {result && (
        <ResultModal
          result={result}
          wrongCount={result.total - result.ok}
          onExport={exportWrong}
          onClose={() => setResult(null)}
          onRestart={restart}
        />
      )}
    </>
  );
}

export default App;
